/**
 * Provider presets for micro-app mounting.
 * Defines which context providers each app needs, so mountReactApp
 * can compose the minimal provider tree per app.
 */
// Internal shared modules
import { AuthProvider } from "../../../context/AuthContext";
import { AccountProvider } from "../../../context/AccountContext";
import { SubscriptionProvider } from "../../../context/SubscriptionContext";
import { PackagesProvider } from "../../../context/PackagesContext";
import { RegistrationProvider } from "../../../context/RegistrationContext";
import { EsimProvisioningProvider } from "../../../context/EsimProvisioningContext";

/**
 * Provider preset names.
 * Each preset defines which context providers wrap the app.
 *
 * Auth + Account + Subscription are the baseline for all presets because
 * useBootstrapSession → useInitialUserData depends on all three.
 *
 * - registration: baseline + Packages + Registration + EsimProvisioning
 * - selfcare:     baseline + Packages
 * - minimal:      baseline only (Activation, Rep Landing)
 */
export const PROVIDER_PRESETS = {
  registration: "registration",
  selfcare: "selfcare",
  minimal: "minimal",
};

/**
 * Wraps children in the provider tree for the given preset.
 * Providers are nested in dependency order (e.g. EsimProvisioning inside Registration).
 *
 * @param {string} preset - One of PROVIDER_PRESETS values.
 * @param {React.ReactNode} children - The content to wrap.
 * @returns {JSX.Element} Provider-wrapped children.
 */
export function buildProviderTree(preset, children) {
  if (preset === PROVIDER_PRESETS.selfcare) {
    return (
      <AuthProvider>
        <AccountProvider>
          <SubscriptionProvider>
            <PackagesProvider>{children}</PackagesProvider>
          </SubscriptionProvider>
        </AccountProvider>
      </AuthProvider>
    );
  }

  if (preset === PROVIDER_PRESETS.minimal) {
    return (
      <AuthProvider>
        <AccountProvider>
          <SubscriptionProvider>{children}</SubscriptionProvider>
        </AccountProvider>
      </AuthProvider>
    );
  }

  // Default: registration — baseline + Packages + Registration + EsimProvisioning
  return (
    <AuthProvider>
      <AccountProvider>
        <SubscriptionProvider>
          <PackagesProvider>
            <RegistrationProvider>
              <EsimProvisioningProvider>{children}</EsimProvisioningProvider>
            </RegistrationProvider>
          </PackagesProvider>
        </SubscriptionProvider>
      </AccountProvider>
    </AuthProvider>
  );
}
